import {
  Box,
  Button,
  Flex,
  FormControl,
  FormHelperText,
  FormLabel,
  HStack,
  Text,
  Grid,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Stack,
  Table,
  TableContainer,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import { CobrancaCltEnum, EtapaNegociacaoStatus, ICompetenciaResumo, INovoVinculoCobranca, IRenegotiationSimulationResponse, IRenegotiationStatusData, IRenegotiationStatusResponse } from "@/models/consignado/proposal-billings.model";
import GridItemDetalhe from "../GridItemDetalhe";
import { BrlCurrencyFormatter } from "@/utils/BrlCurrencyFormatter";
import { documentFormatter } from "@/utils/documentFormatter";
import { formatDateWithNoHours } from "@/utils/formatDateWithNoHours";
import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { buscarStatusRenegociacao, gerarPropostaRenegociacao, realizarAverbacaoRenegociacao, simularRenegociacaoEmprestimo } from "@/services/consignado/proposta-cobranca.service";

type Props = {
  data?: INovoVinculoCobranca[];
  proposalId: string;
  numeroCcb: string;
  competenciasResumo: ICompetenciaResumo[];
  taxaJurosAtual?: number | null;
  etapaNegociacaoStatus?: string | null;
  negociacaoDados?: IRenegotiationStatusData | null;
};

const etapaLabelMap: Record<EtapaNegociacaoStatus, string> = {
  [EtapaNegociacaoStatus.BUSCA_DADOS_VINCULO]: "Busca de dados do vínculo",
  [EtapaNegociacaoStatus.CADASTRAR_VINCULO]: "Cadastrar vínculo",
  [EtapaNegociacaoStatus.GERAR_RENEGOCIACAO]: "Gerar renegociação",
  [EtapaNegociacaoStatus.AVERBACAO]: "Averbação",
  [EtapaNegociacaoStatus.CONCLUIDA]: "Concluída",
};

type AcaoRetentativa = "GERAR_PROPOSTA" | "AVERBAR";

const acaoRetentativaPorEtapa: Partial<Record<EtapaNegociacaoStatus, AcaoRetentativa>> = {
  [EtapaNegociacaoStatus.BUSCA_DADOS_VINCULO]: "GERAR_PROPOSTA",
  [EtapaNegociacaoStatus.CADASTRAR_VINCULO]: "GERAR_PROPOSTA",
  [EtapaNegociacaoStatus.GERAR_RENEGOCIACAO]: "GERAR_PROPOSTA",
  [EtapaNegociacaoStatus.AVERBACAO]: "AVERBAR",
};

const ORDEM_ETAPAS_RETENTATIVA: EtapaNegociacaoStatus[] = [
  EtapaNegociacaoStatus.BUSCA_DADOS_VINCULO,
  EtapaNegociacaoStatus.CADASTRAR_VINCULO,
  EtapaNegociacaoStatus.GERAR_RENEGOCIACAO,
  EtapaNegociacaoStatus.AVERBACAO,
];

const isRenegociacaoConcluida = (
  status?: IRenegotiationStatusResponse | null
): boolean =>
  status?.negociacaoDados?.etapa?.[EtapaNegociacaoStatus.CONCLUIDA]?.status === "OK" ||
  status?.status?.toUpperCase() === "CONCLUIDA";

const resolverAcaoRetentativa = (
  status?: IRenegotiationStatusResponse | null
): AcaoRetentativa | null => {
  const etapas = status?.negociacaoDados?.etapa;

  if (!etapas || isRenegociacaoConcluida(status))
    return null;

  const etapaTravada = ORDEM_ETAPAS_RETENTATIVA.find(
    (chave) => !!etapas[chave] && etapas[chave]?.status !== "OK"
  );

  return etapaTravada ? acaoRetentativaPorEtapa[etapaTravada] ?? null : null;
};

export default function NovosVinculosAccordion({ data, proposalId, numeroCcb, competenciasResumo, taxaJurosAtual, etapaNegociacaoStatus, negociacaoDados }: Props) {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [numeroParcelas, setNumeroParcelas] = useState<string>("1");
  const [novaTaxaMensal, setNovaTaxaMensal] = useState<string>("");
  const [simulacao, setSimulacao] = useState<IRenegotiationSimulationResponse | null>(null);
  const [renegociacaoGeradaNaSessao, setRenegociacaoGeradaNaSessao] = useState(false);

  const numeroParcelasInicial = useMemo(() => {
    const parcelasPendentes = competenciasResumo.filter(
      (competencia) => competencia.status !== CobrancaCltEnum.SUCCESS
    ).length;

    return String(parcelasPendentes > 0 ? parcelasPendentes : 1);
  }, [competenciasResumo]);

  const taxaAtualFormatada = useMemo(() => {
    if (taxaJurosAtual === null || taxaJurosAtual === undefined)
      return null;

    return `${Number(taxaJurosAtual).toLocaleString("pt-BR", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 4,
    })}%`;
  }, [taxaJurosAtual]);

  const margemDisponivel = data?.[0]?.valor_margem_disponivel ?? null;

  const valorParcelaAtual = useMemo(() => {
    if (!competenciasResumo.length)
      return null;

    const competenciaMaisRecente = competenciasResumo.reduce((maisRecente, competencia) =>
      new Date(competencia.competencia) > new Date(maisRecente.competencia)
        ? competencia
        : maisRecente
    );

    return competenciaMaisRecente.valor_parcela;
  }, [competenciasResumo]);

  useEffect(() => {
    setNumeroParcelas(numeroParcelasInicial);
  }, [numeroParcelasInicial]);

  const renegociacaoInicial = useMemo<IRenegotiationStatusResponse | null>(() => {
    if (!etapaNegociacaoStatus && !negociacaoDados)
      return null;

    return {
      status: etapaNegociacaoStatus ?? null,
      negociacaoDados: negociacaoDados ?? null,
    };
  }, [etapaNegociacaoStatus, negociacaoDados]);

  const possuiRenegociacao = !!renegociacaoInicial || renegociacaoGeradaNaSessao;

  const { data: renegociacaoStatus } = useQuery<IRenegotiationStatusResponse>({
    queryKey: ["renegociacao-status", proposalId],
    queryFn: () => buscarStatusRenegociacao(proposalId),
    enabled:
      !!proposalId && possuiRenegociacao && !isRenegociacaoConcluida(renegociacaoInicial),
    initialData: renegociacaoInicial ?? undefined,
    refetchOnWindowFocus: false,
    retry: 0,
    refetchInterval(query) {
      const currentData = query.state.data as IRenegotiationStatusResponse | undefined;
      return isRenegociacaoConcluida(currentData) ? false : 10000;
    },
  });

  const renegociacaoConcluida = isRenegociacaoConcluida(renegociacaoStatus);
  const acaoRetentativa = resolverAcaoRetentativa(renegociacaoStatus);

  const mutationRealizarAverbacao = useMutation({
    mutationFn: () => realizarAverbacaoRenegociacao(proposalId),
    onSuccess() {
      queryClient.invalidateQueries({ queryKey: ["renegociacao-status", proposalId] });
      toast({
        title: "Averbação solicitada",
        description: "A averbação foi solicitada novamente. O status segue sendo monitorado.",
        status: "success",
        duration: 4000,
        isClosable: true,
      });
    },
    onError(error: any) {
      toast({
        title: "Erro ao averbar renegociação",
        description: error?.response?.data?.message || "Não foi possível solicitar a averbação novamente.",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    },
  });

  const mutationSimularRenegociacao = useMutation({
    mutationFn: simularRenegociacaoEmprestimo,
    onSuccess(data) {
      setSimulacao(data);
    },
    onError(error: any) {
      toast({
        title: "Erro ao simular renegociação",
        description: error?.response?.data?.message || "Não foi possível simular a renegociação.",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    },
  });

  const mutationGerarRenegociacao = useMutation({
    mutationFn: gerarPropostaRenegociacao,
    onSuccess() {
      setRenegociacaoGeradaNaSessao(true);
      setSimulacao(null);
      onClose();
      toast({
        title: "Renegociação gerada",
        description: "A proposta de renegociação foi gerada com sucesso.",
        status: "success",
        duration: 4000,
        isClosable: true,
      });
    },
    onError(error: any) {
      toast({
        title: "Erro ao gerar renegociação",
        description: error?.response?.data?.message || "Não foi possível gerar a proposta de renegociação.",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    },
  });

  const safeFormatDate = (date?: string | Date | null): string => {
    if (!date)
      return "-";

    const formattedDate = formatDateWithNoHours(String(date));

    return formattedDate || "-";
  };

  const handleOpenModal = () => {
    setNumeroParcelas(numeroParcelasInicial);
    setNovaTaxaMensal("");
    setSimulacao(null);
    onOpen();
  };

  const handleReabrirSimulacao = () => {
    const parcelasDaNegociacao = renegociacaoStatus?.negociacaoDados?.dados?.numeroParcelas;

    setNumeroParcelas(
      parcelasDaNegociacao ? String(parcelasDaNegociacao) : numeroParcelasInicial
    );
    setNovaTaxaMensal("");
    setSimulacao(null);
    onOpen();
  };

  const handleCloseModal = () => {
    setSimulacao(null);
    onClose();
  };

  const resolverTaxaMensal = (): { isValid: boolean; taxaMensal?: number } => {
    const taxaInformada = novaTaxaMensal.trim().replace(",", ".");

    if (!taxaInformada)
      return { isValid: true, taxaMensal: taxaJurosAtual ?? undefined };

    const taxaConvertida = Number(taxaInformada);

    if (Number.isNaN(taxaConvertida) || taxaConvertida <= 0)
      return { isValid: false };

    return { isValid: true, taxaMensal: taxaConvertida };
  };

  const notificarTaxaInvalida = () => {
    toast({
      title: "Taxa de juros inválida",
      description: "Informe uma taxa mensal maior que zero ou deixe o campo vazio para manter a taxa atual.",
      status: "warning",
      duration: 3000,
      isClosable: true,
    });
  };

  const handleSimularRenegociacao = () => {
    const parcelas = Number(numeroParcelas);

    if (!parcelas || parcelas <= 0) {
      toast({
        title: "Número de parcelas inválido",
        description: "Informe um número de parcelas maior que zero.",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    const { isValid, taxaMensal } = resolverTaxaMensal();

    if (!isValid) {
      notificarTaxaInvalida();
      return;
    }

    mutationSimularRenegociacao.mutate({
      contrato: numeroCcb,
      numeroParcelas: parcelas,
      ...(taxaMensal !== undefined && { taxaMensal }),
    });
  };

  const handleGerarRenegociacao = () => {
    const parcelas = Number(numeroParcelas);

    const { isValid, taxaMensal } = resolverTaxaMensal();

    if (!isValid) {
      notificarTaxaInvalida();
      return;
    }

    mutationGerarRenegociacao.mutate({
      contrato: numeroCcb,
      numeroParcelas: parcelas,
      ...(taxaMensal !== undefined && { taxaMensal }),
    });
  };

  const renegociacaoEtapas = Object.entries(
    renegociacaoStatus?.negociacaoDados?.etapa ?? {}
  ) as Array<[EtapaNegociacaoStatus, { status: string; mensagem?: string }] >;

  return (
    <Stack spacing={4}>
      {possuiRenegociacao && (
        <Box border="1px solid" borderColor="blue.100" borderRadius="lg" bg="blue.50" p={4}>
          <Stack spacing={3}>
            <HStack justify="space-between" align="flex-start">
              <Box>
                <Text fontSize="sm" fontWeight="bold" color="blue.800">
                  Status da renegociação
                </Text>
                <Text fontSize="sm" color="blue.700">
                  {renegociacaoStatus?.status ?? "Aguardando atualização..."}
                </Text>
              </Box>

              {renegociacaoConcluida ? (
                <Text fontSize="sm" fontWeight="semibold" color="green.600">
                  Renegociação concluída com sucesso
                </Text>
              ) : (
                <Text fontSize="xs" color="blue.700">
                  Atualizando a cada 10 segundos
                </Text>
              )}
            </HStack>

            {renegociacaoEtapas.length ? (
              <Stack spacing={2}>
                {renegociacaoEtapas.map(([etapaKey, etapa]) => (
                  <Box key={etapaKey} borderRadius="md" bg="white" p={3}>
                    <Text fontSize="sm" fontWeight="semibold" color="gray.700">
                      {etapaLabelMap[etapaKey]}
                    </Text>
                    <Text fontSize="sm" color="gray.600">
                      Status: {etapa.status}
                    </Text>
                    {etapa.mensagem ? (
                      <Text fontSize="sm" color="gray.600">
                        Mensagem: {etapa.mensagem}
                      </Text>
                    ) : null}
                  </Box>
                ))}
              </Stack>
            ) : null}

            {acaoRetentativa ? (
              <Flex justify="flex-end">
                <Button
                  size="sm"
                  colorScheme="primary"
                  onClick={
                    acaoRetentativa === "AVERBAR"
                      ? () => mutationRealizarAverbacao.mutate()
                      : handleReabrirSimulacao
                  }
                  isLoading={
                    acaoRetentativa === "AVERBAR" && mutationRealizarAverbacao.isPending
                  }
                  loadingText="Averbando..."
                >
                  Tentar novamente
                </Button>
              </Flex>
            ) : null}
          </Stack>
        </Box>
      )}

      {!possuiRenegociacao && (
        <Flex justify="flex-end">
          <Button colorScheme="primary" size="sm" onClick={handleOpenModal}>
            Simular renegociação
          </Button>
        </Flex>
      )}

      {data?.length === 0 && (
        <Text px={4} fontSize="sm" fontWeight='normal'>
          Não há nenhum novo vínculo registrado no momento.
        </Text>
      )}
      
      {data?.map((vinculo) => (
        <Box
          key={vinculo.id}
          border="1px solid"
          borderColor="gray.200"
          borderRadius="lg"
          bg="gray.50"
          overflow="hidden"
        >
          <Box
            px={4}
            py={3}
            borderBottom="1px solid"
            borderColor="gray.200"
            bg="white"
          >
            <Text fontSize="sm" fontWeight="bold">
              {vinculo.nome_empregador ?? "-"}
            </Text>
          </Box>

          <Box
            p={4}
            border="1px solid"
            borderColor="gray.200"
            borderRadius="md"
            m={4}
            bg="white"
          >
            <Grid templateColumns="repeat(4, 1fr)" gap={6}>
              <GridItemDetalhe
                label="CNPJ"
                value={
                  vinculo?.numero_inscricao_empregador
                    ? documentFormatter(vinculo.numero_inscricao_empregador)
                    : "-"
                }
              />

              <GridItemDetalhe
                label="ELEGÍVEL"
                value={vinculo?.elegivel ? "SIM" : "NÃO"}
              />

              <GridItemDetalhe
                label="MARGEM DISPONÍVEL"
                value={
                  vinculo?.valor_margem_disponivel
                    ? BrlCurrencyFormatter(vinculo.valor_margem_disponivel)
                    : "-"
                }
              />

              <GridItemDetalhe
                label="QTD EMPRÉSTIMOS"
                value={vinculo?.qtd_emprestimos}
              />

              <GridItemDetalhe
                label="RAZÃO SOCIAL"
                value={vinculo?.nome_empregador}
              />

              <GridItemDetalhe
                label="BASE MARGEM"
                value={
                  vinculo?.valor_base_margem
                    ? BrlCurrencyFormatter(vinculo.valor_base_margem)
                    : "-"
                }
              />

              <GridItemDetalhe
                label="DATA ADMISSÃO"
                value={safeFormatDate(vinculo?.data_admissao)}
              />
            </Grid>
          </Box>
        </Box>
      ))}

      <Modal isOpen={isOpen} onClose={handleCloseModal} size="6xl" isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Simular renegociação</ModalHeader>
          <ModalCloseButton />

          <ModalBody>
            <Stack spacing={4}>

              <FormControl>
                <FormLabel fontSize="sm">Número de parcelas</FormLabel>
                <Input
                  type="number"
                  min={1}
                  value={numeroParcelas}
                  onChange={(event) => setNumeroParcelas(event.target.value)}
                />
              </FormControl>

              <FormControl>
                <FormLabel fontSize="sm">Nova taxa de juros mensal (%)</FormLabel>
              <Box border="1px solid" borderColor="gray.200" borderRadius="md" bg="gray.50" p={3} mb={2}>
                <Text fontSize="sm" color="gray.700">
                  Taxa de juros atual do contrato:{" "}
                  <Text as="span" fontWeight="bold">
                    {taxaAtualFormatada ?? "-"}
                  </Text>
                </Text>
              </Box>
                <Input
                  type="number"
                  step="0.01"
                  min={0}
                  placeholder={taxaAtualFormatada ?? "Informe a nova taxa"}
                  value={novaTaxaMensal}
                  onChange={(event) => setNovaTaxaMensal(event.target.value)}
                />
                <FormHelperText fontSize="xs">
                  {taxaAtualFormatada
                    ? `Deixe em branco para manter a taxa atual (${taxaAtualFormatada}).`
                    : "Deixe em branco para manter a taxa atual do contrato."}
                </FormHelperText>
              </FormControl>

              <Flex justify="flex-start" align="center" gap={10}>
                <Button
                  colorScheme="primary"
                  size="sm"
                  onClick={handleSimularRenegociacao}
                  isLoading={mutationSimularRenegociacao.isPending}
                  loadingText="Simulando..."
                >
                  Simular renegociação
                </Button>

                <GridItemDetalhe
                  label="Margem disponível"
                  value={
                    margemDisponivel !== null
                      ? BrlCurrencyFormatter(margemDisponivel)
                      : "-"
                  }
                />

                <GridItemDetalhe
                  label="Valor da parcela atual"
                  value={
                    valorParcelaAtual !== null
                      ? BrlCurrencyFormatter(valorParcelaAtual)
                      : "-"
                  }
                />
              </Flex>

              {simulacao?.Conteudo?.Simulacao?.Parcelas?.length ? (
                <>
                  <Box>
                    <Text fontSize="sm" fontWeight="bold">
                      Primeiro vencimento: {safeFormatDate(simulacao.Conteudo.Simulacao.DtPrimeiroVencimento)}
                    </Text>
                  </Box>

                  <TableContainer border="1px solid" borderColor="gray.200" borderRadius="md">
                    <Table size="sm" variant="simple">
                      <Thead>
                        <Tr>
                          <Th>Parcela</Th>
                          <Th>Dias</Th>
                          <Th>Vencimento</Th>
                          <Th>Saldo Devedor</Th>
                          <Th>Amortização</Th>
                          <Th>Juros</Th>
                          <Th>Prestação Total</Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {simulacao.Conteudo.Simulacao.Parcelas.map((parcela) => (
                          <Tr key={parcela.Parcela}>
                            <Td>{parcela.Parcela}</Td>
                            <Td>{parcela.Dias}</Td>
                            <Td>{safeFormatDate(parcela.DtVencimento)}</Td>
                            <Td>{BrlCurrencyFormatter(parcela.VlrSaldoDevedor)}</Td>
                            <Td>{BrlCurrencyFormatter(parcela.VlrAmortizacao)}</Td>
                            <Td>{BrlCurrencyFormatter(parcela.VlrJuros)}</Td>
                            <Td>{BrlCurrencyFormatter(parcela.VlrPrestacaoTotal)}</Td>
                          </Tr>
                        ))}
                      </Tbody>
                    </Table>
                  </TableContainer>
                </>
              ) : null}
            </Stack>
          </ModalBody>

          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={handleCloseModal} size="sm">
              Cancelar
            </Button>
            <Button
              colorScheme="primary"
              onClick={handleGerarRenegociacao}
              size="sm"
              isDisabled={!simulacao || data?.length === 0}
              isLoading={mutationGerarRenegociacao.isPending}
              loadingText="Gerando..."
            >
              Gerar renegociação
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Stack>
  );
}